import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHBoolean from "_shared/booleans/boolean";
import DateSchema from "_shared/dates/date-schema";
import EntityMember from "_shared/entity-members/entity-member";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategorySchema from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import Guid from "_shared/guids/guid";
import Percent from "_shared/percentages/percent";
import {
  FeeScheduleCategoryMaskedCurrency,
  MaskedContracts,
  MaskedDiscount,
  MaskedFeeSchedule,
  MaskedTeam,
} from "work/masked-fields";
import WorkDocumentSchema from "work/work-agreements/contracts/_schemas/work-document-schema";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientTeamSchema from "work/work-agreements/teams/client-team/_schema/client-team-schema";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import TeamLeaderHandoverSchema from "work/work-agreements/teams/leader-handover/_schemas/team-leader-handover-schema";
import TeamLeaderHandover from "work/work-agreements/teams/leader-handover/team-leader-handover";
import VendorTeamDepartureSchema from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-departure-schema";
import VendorTeamSchema from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-schema";
import VendorTeamDeparture from "work/work-agreements/teams/vendor-team/vendor-team-departure";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import { z } from "zod";

export type MaskedField = z.infer<typeof MaskedFieldZ>;
export type WorkAgreementJSON = z.infer<typeof WorkAgreementJSONZ>;
export type WorkAgreementParams = z.infer<typeof WorkAgreementZ>;

export const MaskedFieldZ = z.object({ isMasked: z.literal(true) });

export const WorkAgreementJSONZ = z.object({
  id: z.uuid().nullish(),
  version: z.number().int().nonnegative().nullish(),
  /**
   * Which version of the work this agreement is, from 1 (#6636). Null until it is hired. Not the
   * concurrency `version` above.
   */
  versionNumber: z.number().int().positive().nullish(),
  /**
   * The version this agreement becomes, or became, when it is hired (#6636). Zero or absent from a
   * payload older than the field, or from a view kept before versions existed.
   */
  proposedVersionNumber: z.number().int().nonnegative().nullish(),
  isUnauthorized: z.boolean().nullish(),
  RFPId: z.guid().nullish(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  clientTeam: z
    .intersection(ClientTeamSchema.ClientTeamJSONZ.optional(), z.object({ isMasked: z.boolean() }))
    .nullish(),
  additionalVendors: z.array(EntityMemberSchema.EntityMemberJSONZ).nullish(),
  vendorTeam: z
    .intersection(VendorTeamSchema.VendorTeamJSONZ.optional(), z.object({ isMasked: z.boolean() }))
    .nullish(),
  /**
   * Everyone who has left the vendor team, and why each one left (#5663). Empty for a reader the
   * server masks the vendor team from, and absent from a payload older than the field.
   */
  vendorTeamDepartures: z.array(VendorTeamDepartureSchema.VendorTeamDepartureJSONZ).nullish(),
  /**
   * The handovers of a team's leadership still waiting for an answer that this reader may know about
   * (#5836). Absent from a payload older than the field.
   */
  pendingLeaderHandovers: z.array(TeamLeaderHandoverSchema.TeamLeaderHandoverJSONZ).nullish(),
  teamRestricted: z
    .object({
      isMasked: z.boolean(),
      value: z.boolean().nullish(),
    })
    .nullish(),
  feeSchedule: z.object({
    isMasked: z.boolean(),
    categories: z.array(FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ).nullish(),
  }),
  startDate: z.iso.datetime({ offset: true }).nullish(),
  endDate: z.iso.datetime({ offset: true }).nullish(),
  discount: z.object({ percent: z.number().nullable(), isMasked: z.boolean() }).nullish(),
  clientPolicies: z
    .object({
      isMasked: z.boolean(),
      documents: z
        .array(
          z.object({
            documentId: z.uuid().nullish(),
            documentInfo: WorkDocumentSchema.WorkDocumentJSONZ.nullish(),
            replacesId: z.uuid().nullish(),
            isAvailable: z.boolean().nullish(),
          }),
        )
        .optional(),
    })
    .nullish(),
  vendorPolicies: z
    .object({
      isMasked: z.boolean(),
      documents: z
        .array(
          z.object({
            documentId: z.uuid().nullish(),
            documentInfo: WorkDocumentSchema.WorkDocumentJSONZ.nullish(),
            replacesId: z.uuid().nullish(),
            isAvailable: z.boolean().nullish(),
          }),
        )
        .optional(),
    })
    .nullish(),
  conflictsCheckWaived: z
    .object({
      isMasked: z.boolean(),
      value: z.boolean().nullish(),
    })
    .nullish(),
  conflicts: z
    .object({
      isMasked: z.boolean(),
      documents: z
        .array(
          z.object({
            documentId: z.uuid().nullish(),
            documentInfo: WorkDocumentSchema.WorkDocumentJSONZ.nullish(),
            replacesId: z.uuid().nullish(),
            isAvailable: z.boolean().nullish(),
          }),
        )
        .optional(),
    })
    .nullish(),
  clientTeamTemplateIds: z.array(z.uuid()).nullish(),
  vendorTeamTemplateIds: z.array(z.uuid()).nullish(),
  clientFeeScheduleTemplateIds: z.array(z.uuid()).nullish(),
  vendorFeeScheduleTemplateIds: z.array(z.uuid()).nullish(),
  hireDate: z.iso.datetime({ offset: true }).nullish(),
  archiveDate: z.iso.datetime({ offset: true }).nullish(),
  /** When the reader left this work, on the view they kept of it (#5833). Absent on a live read. */
  frozenAt: z.iso.datetime({ offset: true }).nullish(),
  replacesWorkAgreementId: z.uuid().nullish(),
  replacedByWorkAgreementId: z.uuid().nullish(),
  openRevisionProposalId: z.uuid().nullish(),
  conversationWorkAgreementId: z.uuid().nullish(),
  /**
   * The proposal that was hired to create this agreement. Absent only when that proposal cannot be
   * read -- a data integrity gap, not an ordinary outcome.
   */
  sourceProposalId: z.uuid().nullish(),
});

export const WorkAgreementZ = z
  .object({
    id: z.instanceof(Guid).optional(),
    version: z.number().int().nonnegative().optional(),
    versionNumber: z.number().int().positive().optional(),
    proposedVersionNumber: z.number().int().positive().optional(),
    RFPId: z.instanceof(Guid).optional(),
    name: z.instanceof(ProjectName).optional(),
    description: z.instanceof(ProjectDescription).optional(),
    clientTeam: z.instanceof(ClientTeam).or(z.instanceof(MaskedTeam)).optional(),
    additionalVendors: z.array(z.instanceof(EntityMember)).optional(),
    vendorTeam: z.instanceof(VendorTeam).or(z.instanceof(MaskedTeam)).optional(),
    vendorTeamDepartures: z.array(z.instanceof(VendorTeamDeparture)).optional(),
    pendingLeaderHandovers: z.array(z.instanceof(TeamLeaderHandover)).optional(),
    teamRestricted: z.instanceof(AHBoolean).or(z.instanceof(MaskedTeam)).optional(),
    feeSchedule: z
      .union([
        z.union([z.array(z.instanceof(FeeScheduleCategory)), z.array(z.instanceof(FeeScheduleCategoryMaskedCurrency))]),
        z.instanceof(MaskedFeeSchedule),
      ])
      .optional(),
    startDate: DateSchema.type.nullable().optional(),
    endDate: DateSchema.type.nullable().optional(),
    discount: z.union([z.custom<Percent>(), z.instanceof(MaskedDiscount)]).optional(),
    clientPolicies: z.union([z.array(z.instanceof(WorkDocument)), z.instanceof(MaskedContracts)]).optional(),
    vendorPolicies: z.union([z.array(z.instanceof(WorkDocument)).optional(), z.instanceof(MaskedContracts)]).optional(),
    conflictsCheckWaived: z.union([z.custom<AHBoolean>(), z.instanceof(MaskedContracts)]).optional(),
    conflicts: z.union([z.array(z.instanceof(WorkDocument)), z.instanceof(MaskedContracts)]).optional(),
    clientTeamTemplateIds: z.array(z.instanceof(Guid)).optional(),
    vendorTeamTemplateIds: z.array(z.instanceof(Guid)).optional(),
    clientFeeScheduleTemplateIds: z.array(z.instanceof(Guid)).optional(),
    vendorFeeScheduleTemplateIds: z.array(z.instanceof(Guid)).optional(),
    hireDate: DateSchema.type.optional(),
    archiveDate: DateSchema.type.optional(),
    frozenAt: DateSchema.type.optional(),
    replacesWorkAgreementId: z.instanceof(Guid).optional(),
    replacedByWorkAgreementId: z.instanceof(Guid).optional(),
    openRevisionProposalId: z.instanceof(Guid).optional(),
    conversationWorkAgreementId: z.instanceof(Guid).optional(),
    sourceProposalId: z.instanceof(Guid).optional(),
  })
  .readonly();

export function validate(values: unknown): WorkAgreementParams {
  return parseOrThrow(WorkAgreementZ, values, {
    logError: true,
    schemaName: "WorkAgreement",
    operation: "validate",
    payloadSample: values,
  });
}

export function validateJSON(json: unknown): WorkAgreementJSON {
  return parseOrThrow(WorkAgreementJSONZ, json, {
    logError: true,
    schemaName: "WorkAgreement",
    operation: "validateJSON",
    payloadSample: json,
  });
}

export function isValid(values: unknown): values is WorkAgreementParams {
  try {
    validate(values);
    return true;
  } catch {
    return false;
  }
}

export function isValidJSON(json: unknown): json is WorkAgreementJSON {
  try {
    validateJSON(json);
    return true;
  } catch {
    return false;
  }
}
